import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, FileText, CheckCircle, AlertCircle, TrendingUp, Lightbulb,
  Activity, Beaker, Calendar, Target, Shield, Heart, Dna,
  ChevronRight, BarChart3
} from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { reportsAPI } from '../lib/api';
import { formatDate, reportTypeLabel } from '../lib/utils';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

const reportConfig = {
  gut_microbiome: {
    icon: Activity,
    gradient: 'from-[#8DA399] to-[#6B8A7A]',
    color: '#8DA399',
    lightColor: '#8DA399/20',
    title: 'Gut Microbiome Analysis',
    description: 'Comprehensive analysis of your gut bacterial composition and diversity.',
  },
  vaginal_microbiome: {
    icon: Beaker,
    gradient: 'from-[#E8A095] to-[#D68C82]',
    color: '#E8A095',
    lightColor: '#E8A095/20',
    title: 'Vaginal Microbiome Analysis',
    description: 'Detailed analysis of your vaginal microbiome health and balance.',
  },
  other: {
    icon: FileText,
    gradient: 'from-[#95B8E8] to-[#7AA3D6]',
    color: '#95B8E8',
    lightColor: '#95B8E8/20',
    title: 'Health Report',
    description: 'Comprehensive health analysis and insights.',
  },
};

export default function ReportDetailPage() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadReport();
  }, [reportId]);

  const loadReport = async () => {
    try {
      const response = await reportsAPI.getById(reportId);
      setReport(response.data);
    } catch (error) {
      console.error('Failed to load report:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  if (!report) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-[#4A2C2A]/60">Report not found</p>
          <Link to="/reports">
            <Button variant="ghost" className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Reports
            </Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const config = reportConfig[report.report_type] || reportConfig.other;
  const Icon = config.icon;
  const analysis = report.analysis || {};
  const displayDate = report.report_date || report.created_at;
  
  // Calculate counts for stats
  const positiveCount = analysis.positive_indicators?.length || 0;
  const concernCount = analysis.concern_areas?.length || 0;
  
  // Data for charts
  const distributionData = [
    { name: 'Positive', value: positiveCount, color: '#8DA399' },
    { name: 'Attention', value: concernCount, color: '#E8C495' },
  ].filter(d => d.value > 0);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'findings', label: 'Findings', icon: TrendingUp },
    { id: 'recommendations', label: 'Actions', icon: Target },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="report-detail-page">
        {/* Back Link */}
        <Link to="/reports" className="inline-flex items-center text-[#4A2C2A]/60 hover:text-[#4A2C2A] transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Reports
        </Link>

        {/* Hero Header */}
        <div className={`relative overflow-hidden bg-gradient-to-br ${config.gradient} rounded-3xl p-8 text-white`}>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
              <div className="flex items-start gap-5">
                <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="text-white/70 text-sm font-medium uppercase tracking-wider mb-1">
                    {reportTypeLabel(report.report_type)}
                  </p>
                  <h1 className="font-heading text-3xl lg:text-4xl mb-2">{report.title}</h1>
                  <div className="flex items-center gap-4 text-sm text-white/70">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(displayDate)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="flex gap-4">
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center min-w-[80px]">
                  <p className="text-2xl font-heading">{positiveCount}</p>
                  <p className="text-xs text-white/70">Positive</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center min-w-[80px]">
                  <p className="text-2xl font-heading">{concernCount}</p>
                  <p className="text-xs text-white/70">Attention</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 bg-[#FDFBF7] rounded-xl p-1.5">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-[#4A2C2A] shadow-sm'
                  : 'text-[#4A2C2A]/60 hover:text-[#4A2C2A]'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6 animate-fade-up">
            {/* Summary Card */}
            {report.summary && (
              <div className="soft-card p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#E8A095]/20 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-[#E8A095]" />
                  </div>
                  <h2 className="font-heading text-xl text-[#4A2C2A]">Summary</h2>
                </div>
                <p className="text-[#4A2C2A]/80 leading-relaxed text-lg">{report.summary}</p>
              </div>
            )}

            {/* Stats Grid */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="soft-card p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-[#8DA399]/20 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-[#8DA399]" />
                </div>
                <p className="text-3xl font-heading text-[#4A2C2A] mb-1">{positiveCount}</p>
                <p className="text-sm text-[#4A2C2A]/60">Positive Indicators</p>
              </div>
              <div className="soft-card p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-[#E8C495]/20 flex items-center justify-center mx-auto mb-3">
                  <AlertCircle className="w-6 h-6 text-[#E8C495]" />
                </div>
                <p className="text-3xl font-heading text-[#4A2C2A] mb-1">{concernCount}</p>
                <p className="text-sm text-[#4A2C2A]/60">Areas of Attention</p>
              </div>
              <div className="soft-card p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-[#E8A095]/20 flex items-center justify-center mx-auto mb-3">
                  <Lightbulb className="w-6 h-6 text-[#E8A095]" />
                </div>
                <p className="text-3xl font-heading text-[#4A2C2A] mb-1">{analysis.recommendations?.length || 0}</p>
                <p className="text-sm text-[#4A2C2A]/60">Recommendations</p>
              </div>
            </div>

            {/* Distribution Chart */}
            {distributionData.length > 0 && (
              <div className="soft-card p-6">
                <h3 className="font-heading text-lg text-[#4A2C2A] mb-6">Analysis Overview</h3>
                <div className="flex items-center gap-8">
                  <div className="w-40 h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={distributionData}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={70}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {distributionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-3">
                    {distributionData.map((item, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="text-[#4A2C2A]/80">{item.name}</span>
                        </div>
                        <span className="font-medium text-[#4A2C2A]">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'findings' && (
          <div className="space-y-6 animate-fade-up">
            {/* Key Findings */}
            {analysis.key_findings?.length > 0 && (
              <div className="soft-card p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#95B8E8]/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-[#95B8E8]" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl text-[#4A2C2A]">Key Findings</h2>
                    <p className="text-sm text-[#4A2C2A]/60">Important discoveries from your analysis</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {analysis.key_findings.map((finding, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 bg-[#FDFBF7] rounded-xl hover:bg-[#95B8E8]/5 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-[#95B8E8]/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-medium text-[#95B8E8]">{i + 1}</span>
                      </div>
                      <p className="text-[#4A2C2A]/80 leading-relaxed">{finding}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Positive Indicators */}
            {analysis.positive_indicators?.length > 0 && (
              <div className="soft-card p-6 border-l-4 border-[#8DA399]">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#8DA399]/20 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-[#8DA399]" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl text-[#4A2C2A]">Positive Indicators</h2>
                    <p className="text-sm text-[#8DA399]">Things that are going well</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {analysis.positive_indicators.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-[#8DA399]/5 rounded-xl">
                      <CheckCircle className="w-5 h-5 text-[#8DA399] flex-shrink-0 mt-0.5" />
                      <span className="text-[#4A2C2A]/80">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Areas of Attention */}
            {analysis.concern_areas?.length > 0 && (
              <div className="soft-card p-6 border-l-4 border-[#E8C495]">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#E8C495]/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#E8C495]" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl text-[#4A2C2A]">Areas of Attention</h2>
                    <p className="text-sm text-[#E8C495]">Items to monitor or address</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {analysis.concern_areas.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-[#E8C495]/5 rounded-xl">
                      <AlertCircle className="w-5 h-5 text-[#E8C495] flex-shrink-0 mt-0.5" />
                      <span className="text-[#4A2C2A]/80">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Raw Analysis fallback */}
            {analysis.raw_analysis && !analysis.key_findings?.length && (
              <div className="soft-card p-6">
                <h2 className="font-heading text-xl text-[#4A2C2A] mb-4">Analysis</h2>
                <p className="text-[#4A2C2A]/70 whitespace-pre-wrap leading-relaxed">{analysis.raw_analysis}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'recommendations' && (
          <div className="space-y-6 animate-fade-up">
            {analysis.recommendations?.length > 0 ? (
              <div className="soft-card p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#E8A095]/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-[#E8A095]" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl text-[#4A2C2A]">Personalized Recommendations</h2>
                    <p className="text-sm text-[#4A2C2A]/60">Action steps based on your results</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {analysis.recommendations.map((rec, i) => (
                    <div key={i} className="relative overflow-hidden p-5 bg-gradient-to-r from-[#FDFBF7] to-white rounded-xl border border-[#E8A095]/10 hover:border-[#E8A095]/30 transition-all">
                      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#E8A095] to-[#D68C82]" />
                      <div className="flex items-start gap-4 pl-4">
                        <div className="w-10 h-10 rounded-full bg-[#E8A095]/10 flex items-center justify-center flex-shrink-0">
                          <span className="font-heading text-[#E8A095]">{i + 1}</span>
                        </div>
                        <div>
                          <p className="text-[#4A2C2A] leading-relaxed">{rec}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="soft-card p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-[#E8A095]/10 flex items-center justify-center mx-auto mb-6">
                  <Lightbulb className="w-8 h-8 text-[#E8A095]" />
                </div>
                <h3 className="font-heading text-xl text-[#4A2C2A] mb-2">No Specific Recommendations</h3>
                <p className="text-[#4A2C2A]/60 max-w-md mx-auto">
                  This report doesn't have specific recommendations. Chat with your wellness advisor for personalized guidance.
                </p>
                <Link to="/chat">
                  <Button className="mt-6 bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-full px-6">
                    Talk to Advisor <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            )}

            {/* CTA Card */}
            <div className="bg-gradient-to-r from-[#4A2C2A] to-[#2D1A19] rounded-2xl p-8 text-white">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Dna className="w-8 h-8 text-[#E8A095]" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="font-heading text-xl mb-2">Have Questions About Your Results?</h3>
                  <p className="text-white/70">
                    Your wellness advisor can help explain your report and provide additional guidance.
                  </p>
                </div>
                <Link to="/chat">
                  <Button className="bg-[#E8A095] text-[#4A2C2A] hover:bg-[#D68C82] rounded-full px-6 whitespace-nowrap">
                    Ask Advisor
                  </Button>
                </Link>
              </div>
            </div>

            {/* Specific Advice from Provider */}
            {report.specific_advice && (
              <div className="soft-card p-6 border-l-4 border-[#E8A095]">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#E8A095]/20 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-[#E8A095]" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl text-[#4A2C2A]">Personalized Advice from Your Provider</h2>
                    <p className="text-sm text-[#4A2C2A]/60">Specific guidance tailored for you</p>
                  </div>
                </div>
                <p className="text-[#4A2C2A]/80 leading-relaxed whitespace-pre-wrap">{report.specific_advice}</p>
              </div>
            )}
          </div>
        )}

        {/* Disclaimer */}
        <div className="bg-[#FDFBF7] rounded-xl p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-[#4A2C2A]/10 flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-[#4A2C2A]/60" />
          </div>
          <div>
            <p className="text-sm text-[#4A2C2A]/70">
              <strong className="text-[#4A2C2A]">Disclaimer:</strong> This analysis is for informational purposes only and should not replace professional medical advice. 
              Please consult with your healthcare provider for personalized medical recommendations.
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
